import * as React from 'react';
import { timeline } from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import FacetsApplied from '../components/FacetsApplied';
import FacetsCategory, { IFacetsCategory } from '../components/FacetsCategory';
import { useSearch } from '../hooks';
import MobileSort from './MobileSort';
import classNames from 'classnames';

export type FacetsData = Array<IFacetsCategory>;

interface IFacets {
  title: string;
  buttonLabel: string;
  clearLabel: string;
  labelSortBy: string;
  labelSortRelevance: string;
  labelSortAlphabetical: string;
  labelSortRecent: string;
  isDesktop: boolean;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
}

const Facets: React.FC<IFacets> = ({
  title,
  clearLabel,
  buttonLabel,
  labelSortBy,
  labelSortRelevance,
  labelSortAlphabetical,
  labelSortRecent,
  isDesktop,
  isOpen,
  isLoading,
  onClose
}) => {
  const {
    state: { facets }
  } = useSearch();

  const panelEl = React.useRef<HTMLDivElement>(null);

  const toggleFacetsPanel = React.useCallback(async (open: boolean) => {
    const panel = panelEl.current;
    const inner = panelEl.current?.querySelector('.search__facets-inner');

    if (panel && inner) {
      if (open) {
        panelEl.current.style.visibility = 'visible';

        const sequence = [
          [panel, { opacity: 1 }, { duration: 0.2 }],
          [inner, { x: 0 }, { duration: 0.8, easing: 'ease-out' }]
        ] as TimelineDefinition;

        await timeline(sequence, { duration: 0.6 });
      } else {
        const sequence = [
          [inner, { x: '100%' }, { duration: 0.8, easing: 'ease-in' }],
          [panel, { opacity: 0 }, { duration: 0.2 }]
        ] as TimelineDefinition;

        await timeline(sequence, { duration: 0.6 }).finished;
        panelEl.current.style.visibility = 'hidden';
      }
    }
  }, []);

  React.useEffect(() => {
    toggleFacetsPanel(isOpen);
  }, [isOpen]);

  return (
    <div
      className={classNames([
        'search__facets',
        { 'search__facets--hidden': isLoading }
      ])}
      ref={panelEl}
    >
      <div className="search__facets-inner">
        <div className="search__facets-header">
          <p className="search__facets-title">{title}</p>
          {!isDesktop && (
            <button onClick={onClose} aria-label="Close">
              <svg role="presentation">
                <use xlinkHref="#close" />
              </svg>
            </button>
          )}
        </div>

        {!isDesktop && <FacetsApplied clearLabel={clearLabel} />}

        {facets.map((category, index) => (
          <FacetsCategory
            key={category.label}
            {...category}
            expanded={!index}
          />
        ))}

        <MobileSort
          label={labelSortBy}
          labelSortRelevance={labelSortRelevance}
          labelSortAlphabetical={labelSortAlphabetical}
          labelSortRecent={labelSortRecent}
        />

        <button type="button" className="btn btn--primary" onClick={onClose}>
          <span>{buttonLabel}</span>
        </button>
      </div>
    </div>
  );
};

export default Facets;
